import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getClientStatus, isDispatchedStatusOverride, isReceivedStatus } from './service_request_status';
import { formatClientNotes } from './client_notes';

const FILTERS = [
  { id: 'all', label: 'All Requests', description: 'Visible to your account', tone: 'blue' },
  { id: 'pending', label: 'Received', description: 'Received or follow-up is 30+ days away', tone: 'slate' },
  { id: 'scheduling', label: 'Scheduling in Progress', description: 'Being prepared for scheduling', tone: 'amber' },
  { id: 'dispatched', label: 'Dispatched', description: 'A service provider has been dispatched', tone: 'teal' },
  { id: 'complete', label: 'Service Complete', description: 'Finished requests', tone: 'green' }
];

const fieldValue = (ticket, fieldId) => ticket?.[fieldId]?.value ?? '';
export const getServiceRequestDateRangeLabel = (now = new Date()) => {
  const currentYear = Number(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric'
  }).format(new Date(now)));
  return `Follow-up dates: January 1, ${currentYear - 1}–December 31, ${currentYear}`;
};

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const isCompletedTicket = ticket => {
  if (fieldValue(ticket, '_portalApp') === 'fire_pest') return Boolean(fieldValue(ticket, 38));
  const value = fieldValue(ticket, 21);
  const completedFlag = value === true || value === 1 || String(value).toLowerCase() === 'true';
  const textStatus = getClientStatus(fieldValue(ticket, '_statusText'));
  return completedFlag || textStatus.tone === 'complete';
};

const startOfCalendarDay = value => {
  const dateOnlyMatch = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnlyMatch
    ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return Number.NaN;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};
export const getSummaryCategory = (ticket, now = Date.now()) => {
  if (isCompletedTicket(ticket)) return 'complete';
  if (fieldValue(ticket, '_portalApp') === 'fire_pest' && isDispatchedStatusOverride(fieldValue(ticket, 192))) return 'dispatched';
  if (isReceivedStatus(fieldValue(ticket, '_statusText'))) return 'pending';
  const followUpDay = startOfCalendarDay(fieldValue(ticket, 32));
  const today = startOfCalendarDay(now);
  const pendingCutoff = new Date(today);
  pendingCutoff.setDate(pendingCutoff.getDate() + 30);
  if (!Number.isNaN(followUpDay) && followUpDay >= pendingCutoff.getTime()) return 'pending';
  if (getClientStatus(fieldValue(ticket, '_statusText')).tone === 'scheduled') return 'scheduled';
  return 'scheduling';
};
const STATUS_LABELS = { pending: 'Received', scheduling: 'Scheduling in Progress', scheduled: 'Scheduled', dispatched: 'Dispatched', complete: 'Service Complete' };
export const getTicketStatus = ticket => STATUS_LABELS[getSummaryCategory(ticket)];
const getStatusPresentation = ticket => ({ tone: getSummaryCategory(ticket) });

const matchesFilter = (ticket, filter) => {
  if (filter !== 'all') return getSummaryCategory(ticket) === filter;
  return true;
};

const matchesSearch = (ticket, searchTerm) => {
  if (!searchTerm) return true;
  return [10, 15, 18, 7, 26, 27, 38, 192]
    .map(fieldId => String(fieldValue(ticket, fieldId)).toLowerCase())
    .concat(getTicketStatus(ticket).toLowerCase(), String(fieldValue(ticket, '_portalApp')).toLowerCase())
    .some(value => value.includes(searchTerm));
};

const endSession = dispatch => {
  dispatch({ type: 'LOG_OUT', payload: null });
  sessionStorage.removeItem('currentUser');
  window.location = '/';
};

export const ServiceTickets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tickets, setTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loadState, setLoadState] = useState({ status: 'loading', message: '' });

  useEffect(() => {
    let cancelled = false;
    fetch('/apiserviceTickets')
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.status === 401 || data?.sessionExpired) return endSession(dispatch);
        if (!response.ok) throw new Error(data?.message || 'Unable to load service requests.');
        setTickets(Array.isArray(data) ? data : []);
        setLoadState({ status: 'ready', message: '' });
      })
      .catch(error => !cancelled && setLoadState({ status: 'error', message: error.message || 'Unable to load service requests.' }));
    return () => { cancelled = true; };
  }, [dispatch]);

  const counts = useMemo(() => ({
    all: tickets.length,
    pending: tickets.filter(ticket => getSummaryCategory(ticket) === 'pending').length,
    scheduling: tickets.filter(ticket => getSummaryCategory(ticket) === 'scheduling').length,
    dispatched: tickets.filter(ticket => getSummaryCategory(ticket) === 'dispatched').length,
    complete: tickets.filter(ticket => getSummaryCategory(ticket) === 'complete').length
  }), [tickets]);

  const filteredTickets = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return tickets.filter(ticket => matchesFilter(ticket, activeFilter) && matchesSearch(ticket, normalizedSearch));
  }, [activeFilter, searchTerm, tickets]);

  const selectFilter = filter => setActiveFilter(current => current === filter && filter !== 'all' ? 'all' : filter);

  return (
    <div className="tickets serviceRequestsPage">
      <header className="serviceRequestsHeader">
        <div>
          <span className="serviceRequestsEyebrow">Request overview</span>
          <h2>Service Requests</h2>
          <p>Review activity, monitor open work, and find requests that need follow-up.</p>
          <span className="serviceRequestsDateRange">{getServiceRequestDateRangeLabel()}</span>
        </div>
      </header>

      <section className="serviceRequestSummary" aria-label="Service request summary filters">
        {FILTERS.map(filter => (
          <button
            type="button"
            key={filter.id}
            className={`serviceRequestSummaryCard serviceRequestSummaryCard-${filter.tone} ${activeFilter === filter.id ? 'active' : ''}`}
            aria-pressed={activeFilter === filter.id}
            onClick={() => selectFilter(filter.id)}
          >
            <span className="serviceRequestSummaryTop"><span>{filter.label}</span><i aria-hidden="true" /></span>
            <strong>{counts[filter.id]}</strong>
            <small>{filter.description}</small>
          </button>
        ))}
      </section>

      <section className="ticketsList serviceRequestsTableCard">
        <div className="serviceRequestsToolbar">
          <div>
            <h3>{FILTERS.find(filter => filter.id === activeFilter)?.label}</h3>
            <p>{filteredTickets.length} request{filteredTickets.length === 1 ? '' : 's'} shown</p>
          </div>
          <label className="ticketsInputWrapper">
            <span className="srOnly">Search service requests</span>
            <input aria-label="Search service requests" placeholder="Search location, issue, person, or notes…" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} />
          </label>
        </div>

        {loadState.status === 'loading' && <div className="serviceRequestsState" role="status">Loading service requests…</div>}
        {loadState.status === 'error' && <div className="serviceRequestsState serviceRequestsError" role="alert">{loadState.message}</div>}
        {loadState.status === 'ready' && (
          <div className="tableScroll"><table id="tableWrapper" className="serviceRequestsTable">
            <thead><tr>
              <th>Service Area</th><th>Location Code</th><th>Address</th><th>Service Type</th><th>Inspection Date</th><th>Reported By</th><th>Status</th><th>Notes</th>
            </tr></thead>
            <tbody>
              {filteredTickets.map(ticket => {
                const ticketId = fieldValue(ticket, 3);
                const status = getTicketStatus(ticket);
                const statusPresentation = getStatusPresentation(ticket);
                const portalApp = fieldValue(ticket, '_portalApp') || 'waste';
                return (
                  <tr key={`${portalApp}:${ticketId}`} onClick={() => navigate(`/serviceTicket/${ticketId}${portalApp === 'fire_pest' ? '?app=fire_pest' : ''}`, { state: { id: ticketId } })} className="tableRow">
                    <td className="tableCell">{fieldValue(ticket, 27)}</td>
                    <td className="tableCell serviceRequestLocationCode">{fieldValue(ticket, 10)}</td>
                    <td className="tableCell">{fieldValue(ticket, 15)}</td>
                    <td className="tableCell">{fieldValue(ticket, 7)}</td>
                    <td className="tableCell">{fieldValue(ticket, '_portalApp') === 'fire_pest' ? formatDate(fieldValue(ticket, 38)) : '—'}</td>
                    <td className="tableCell">{fieldValue(ticket, 18)}</td>
                    <td className="tableCell"><span className={`serviceRequestStatus serviceRequestStatus-${statusPresentation.tone}`}>{status}</span></td>
                    <td className="tableCell serviceRequestNotesCell">{formatClientNotes(fieldValue(ticket, 26))}</td>
                  </tr>
                );
              })}
              {!filteredTickets.length && <tr><td colSpan="8" className="serviceRequestsEmpty">No service requests match this filter.</td></tr>}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  );
};
